import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import userService from '@/shared/services/userService';
import { hasError } from './blockUtils';

export const MAJOR_ID = 'sa-major';
export const SECOND_MAJOR_ID = 'sa-second-major';
export const MINOR_ID = 'sa-minor';
export const STUDENT_ID_ID = 'sa-student-id';

export const MAX_FIELD_LENGTH = 100;
const NINE_DIGITS = /^\d{9}$/;
export const MALFORMED_STUDENT_ID_MESSAGE =
    'Enter your 900 number: nine digits.';

// answers.academicBackground holds { major, secondMajor, minor } next to the
// fields the wizard already keeps there. answers.profileStudentId is the value
// typed when the profile has none; it belongs to the profile and must never go
// into the application's request body or a saved draft.
// userProfile is the signed-in profile (useAuth().userProfile).
export function getErrors(answers, userProfile) {
    const errors = [];
    const background = answers?.academicBackground || {};
    // In the order the controls appear, so the first one is the one to focus
    if (!userProfile?.studentId) {
        const typed = String(answers?.profileStudentId || '').trim();
        if (!NINE_DIGITS.test(typed)) {
            errors.push({
                id: STUDENT_ID_ID,
                label: 'Student ID (900 number)',
            });
        }
    }
    if (!String(background.major || '').trim()) {
        errors.push({ id: MAJOR_ID, label: 'Major' });
    }
    return errors;
}

export function isComplete(answers, userProfile) {
    return getErrors(answers, userProfile).length === 0;
}

// Called when the step is left. Resolves { saved: true } when there is nothing
// to save or the profile took the value, and { saved: false, field, message }
// with the server's own field and message when it refused. A failure with no
// message from the server resolves with a plain retry sentence.
export async function saveStudentId(answers, userProfile, refreshProfile) {
    if (userProfile?.studentId) return { saved: true };
    const studentId = String(answers?.profileStudentId || '').trim();
    if (!NINE_DIGITS.test(studentId)) {
        return {
            saved: false,
            field: 'studentId',
            message: MALFORMED_STUDENT_ID_MESSAGE,
        };
    }
    try {
        await userService.updateProfile({ studentId });
    } catch (err) {
        const data = err?.response?.data;
        return {
            saved: false,
            field: data?.field || 'studentId',
            message:
                data?.message ||
                'Your student ID could not be saved. Please try again.',
        };
    }
    if (refreshProfile) await refreshProfile();
    return { saved: true };
}

// context.fieldErrors.studentId is the message from saveStudentId. The wizard
// clears it when onChange('profileStudentId', ...) fires.
function AboutYou({ answers, onChange, errorIds, context }) {
    const { userProfile } = useAuth();
    const background = answers.academicBackground || {};
    const setBackground = (field, value) =>
        onChange('academicBackground', { ...background, [field]: value });

    const serverMessage = context?.fieldErrors?.studentId;
    const idInvalid =
        Boolean(serverMessage) || hasError(errorIds, STUDENT_ID_ID);
    const idErrorId = `${STUDENT_ID_ID}-error`;
    const majorInvalid = hasError(errorIds, MAJOR_ID);

    return (
        <div>
            <h2 className="h5 mb-3">About You</h2>
            <dl className="row mb-2">
                <dt className="col-sm-4">Name</dt>
                <dd className="col-sm-8">{userProfile?.name}</dd>
                <dt className="col-sm-4">Email</dt>
                <dd className="col-sm-8">{userProfile?.email}</dd>
                {userProfile?.studentId && (
                    <>
                        <dt className="col-sm-4">Student ID (900 number)</dt>
                        <dd className="col-sm-8">{userProfile.studentId}</dd>
                    </>
                )}
            </dl>
            <p className="mb-4">
                <Link to="/profile/edit" className="d-inline-block py-1">
                    Edit your profile
                </Link>
            </p>

            <Row>
                <Col md={8}>
                    {!userProfile?.studentId && (
                        <Form.Group className="mb-3" controlId={STUDENT_ID_ID}>
                            <Form.Label>
                                Student ID (900 number){' '}
                                <span
                                    className="text-danger"
                                    aria-hidden="true"
                                >
                                    *
                                </span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                maxLength={9}
                                required
                                aria-required="true"
                                value={answers.profileStudentId || ''}
                                onChange={(e) =>
                                    onChange('profileStudentId', e.target.value)
                                }
                                isInvalid={idInvalid}
                                aria-invalid={idInvalid}
                                aria-describedby={
                                    idInvalid ? idErrorId : undefined
                                }
                            />
                            {idInvalid && (
                                <Form.Control.Feedback
                                    type="invalid"
                                    id={idErrorId}
                                    role="alert"
                                >
                                    {serverMessage ||
                                        MALFORMED_STUDENT_ID_MESSAGE}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3" controlId={MAJOR_ID}>
                        <Form.Label>
                            Major{' '}
                            <span className="text-danger" aria-hidden="true">
                                *
                            </span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            required
                            aria-required="true"
                            value={background.major || ''}
                            onChange={(e) =>
                                setBackground('major', e.target.value)
                            }
                            isInvalid={majorInvalid}
                            aria-invalid={majorInvalid}
                            aria-describedby={
                                majorInvalid ? `${MAJOR_ID}-error` : undefined
                            }
                        />
                        {majorInvalid && (
                            <Form.Control.Feedback
                                type="invalid"
                                id={`${MAJOR_ID}-error`}
                                role="alert"
                            >
                                Enter your major.
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3" controlId={SECOND_MAJOR_ID}>
                        <Form.Label>Second major (optional)</Form.Label>
                        <Form.Control
                            type="text"
                            maxLength={MAX_FIELD_LENGTH}
                            value={background.secondMajor || ''}
                            onChange={(e) =>
                                setBackground('secondMajor', e.target.value)
                            }
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId={MINOR_ID}>
                        <Form.Label>Minor (optional)</Form.Label>
                        <Form.Control
                            type="text"
                            maxLength={MAX_FIELD_LENGTH}
                            value={background.minor || ''}
                            onChange={(e) =>
                                setBackground('minor', e.target.value)
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>
        </div>
    );
}

export default AboutYou;
